import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LIBRARY_DISPLAY_NAMES } from '@/shared/constants/libraryConfig';
import { LIBRARY_IMPORT_MAP } from '@/shared/constants/libraryImportMap';
import type {
  DagWorkbenchProps,
  ExperimentWorkbenchProps,
  Library,
  WorkbenchAssets,
  WorkbenchProps,
} from '@/shared/types';

import { useModules } from '@/entities/langserver';

import type { AutoImport } from '@/features/code-analysis';
import { EXPERIMENT_DEFAULT_TEMPLATE, prepareExperimentCode } from '@/features/experiment-workbench/lib';

import { CodeCell } from '@/widgets/workbench/CodeCell';
import { LibraryCell } from '@/widgets/workbench/LibraryCell';
import { LogCell } from '@/widgets/workbench/LogCell';
import { WorkbenchSidebar } from '@/widgets/workbench/WorkbenchSidebar';

import styles from './Workbench.module.css';

const isExperimentProps = (props: WorkbenchProps): props is ExperimentWorkbenchProps => {
  return props.type === 'experiment';
};

const isDagProps = (props: WorkbenchProps): props is DagWorkbenchProps => {
  return props.type === 'dag';
};

export const Workbench: React.FC<WorkbenchProps> = (props) => {
  const {
    initialCode,
    templateCode,
    initialLibraries: initialLibrariesProp,
    onStateChange,
    logs: externalLogs = '',
    isExecuting = false,
    readOnly = false,
    scrollableReadOnlyCode = false,
    executionFiles,
    onExecutionFilesUpdate,
    onLibraryLog,
  } = props;
  const isDag = isDagProps(props);
  const isExperiment = isExperimentProps(props);
  const moduleTarget = isDag ? 'dag' : 'run';
  const dagSignatureCode = isDag ? props.signatureCode : undefined;
  const initialLibraries = initialLibrariesProp ?? [];
  const experimentOnAssetChange = isExperiment ? props.onAssetChange : undefined;
  const clientId = isDag ? props.context.clientId : isExperiment ? props.context.runId : 'demo-user';

  const { data: modulesData } = useModules(moduleTarget, clientId);

  const [availableLibraries, setAvailableLibraries] = useState<string[]>([]);
  const [uploadedModules, setUploadedModules] = useState<string[]>([]);

  const { initialSignature, initialBody } = useMemo(() => {
    if (isExperiment) {
      const prepared = prepareExperimentCode({
        initialCode,
        templateCode: templateCode || EXPERIMENT_DEFAULT_TEMPLATE,
      });
      return { initialSignature: prepared.signature, initialBody: prepared.body };
    }

    if (isDag) {
      const trimmedInitialCode = initialCode?.trim();
      const dagBody = trimmedInitialCode === '' ? templateCode : initialCode;

      return {
        initialSignature: dagSignatureCode ?? '',
        initialBody: dagBody ?? '',
      };
    }

    if (initialCode) {
      return { initialSignature: '', initialBody: initialCode };
    }

    return { initialSignature: '', initialBody: '' };
  }, [dagSignatureCode, initialCode, isDag, isExperiment, templateCode]);

  const [installedLibraries, setInstalledLibraries] = useState<Library[]>(initialLibraries);
  const [signature] = useState<string>(initialSignature);
  const [body, setBody] = useState<string>(initialBody);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; content: string }>>([]);

  const [isLogVisible, setIsLogVisible] = useState(true);
  const [logHeight, setLogHeight] = useState(283);

  useEffect(() => {
    if (modulesData) {
      const venvPackageNames = modulesData.venv_packages.map((pkg) => pkg.name);
      const uploadedModuleNames = modulesData.uploaded.map((module) => module.module_name);

      setAvailableLibraries(venvPackageNames);

      if (isDag || isExperiment) {
        setUploadedModules(uploadedModuleNames);
      } else if (executionFiles !== undefined) {
        const validUploadedModules = uploadedModuleNames.filter((name) => executionFiles[`${name}.py`]);
        setUploadedModules(validUploadedModules);
      } else {
        setUploadedModules([]);
      }

      if (uploadedModuleNames.length > 0 && executionFiles) {
        setInstalledLibraries((prev) => {
          const uploadedModulesSet = new Set(uploadedModuleNames.filter((name) => executionFiles[`${name}.py`]));

          const updatedLibraries = prev.map((lib) => {
            if (uploadedModulesSet.has(lib.name)) {
              return {
                name: lib.name,
                type: 'custom' as const,
                content: executionFiles[`${lib.name}.py`] || '',
              };
            }
            return lib;
          });

          const existingNames = new Set(prev.map((lib) => lib.name));
          const newCustomLibraries = uploadedModuleNames
            .filter((name) => !existingNames.has(name) && executionFiles[`${name}.py`])
            .map((name) => ({
              name,
              type: 'custom' as const,
              content: executionFiles[`${name}.py`] || '',
            }));

          return [...updatedLibraries, ...newCustomLibraries];
        });
      }
    }
  }, [modulesData, executionFiles, isDag, isExperiment]);

  const autoImports = useMemo<AutoImport[]>(() => {
    return installedLibraries.flatMap((lib) => {
      if (lib.type === 'custom') {
        return [{ module: lib.name }];
      }
      return LIBRARY_IMPORT_MAP[lib.name] || [{ module: lib.name }];
    });
  }, [installedLibraries]);

  const libraryDisplayNames = useMemo(() => {
    return LIBRARY_DISPLAY_NAMES;
  }, []);

  useEffect(() => {
    if (!onStateChange) return;

    if (isDag) {
      onStateChange({ code: body, signatureCode: signature, installedLibraries, autoImports, uploadedFiles });
      return;
    }

    const fullCode = `${signature}\n${body}`;
    onStateChange({ code: fullCode, installedLibraries, autoImports, uploadedFiles });
  }, [autoImports, body, installedLibraries, isDag, onStateChange, signature, uploadedFiles]);

  const handleAssetChange = useCallback(
    (assets: WorkbenchAssets) => {
      if (experimentOnAssetChange) {
        experimentOnAssetChange(assets);
      }
    },
    [experimentOnAssetChange]
  );

  const handleInstallLibrary = useCallback(
    (library: string, libraryInfo?: { type: 'custom' | 'standard'; content?: string }) => {
      setInstalledLibraries((prev) => {
        const alreadyInstalled = prev.some((lib) => lib.name === library);
        if (alreadyInstalled) {
          return prev;
        }

        // libraryInfo가 제공되면 그것을 사용, 아니면 기존 로직 사용
        let type: 'custom' | 'standard';
        let content: string;

        if (libraryInfo) {
          type = libraryInfo.type;
          content = libraryInfo.content || '';
        } else {
          // executionFiles에 파일이 있으면 커스텀, 아니면 스탠다드
          const hasFileContent = executionFiles && executionFiles[`${library}.py`];
          const isCustom = hasFileContent || uploadedModules.includes(library);
          type = isCustom ? 'custom' : 'standard';
          content = hasFileContent ? executionFiles[`${library}.py`] : '';
        }

        return [...prev, { name: library, type, content }];
      });

      setIsLogVisible(true);
    },
    [uploadedModules, executionFiles]
  );

  const handleUninstallLibrary = useCallback((library: string) => {
    setInstalledLibraries((prev) => prev.filter((lib) => lib.name !== library));
    setIsLogVisible(true);
  }, []);

  const handleFileUpload = useCallback((fileName: string, fileContent: string) => {
    setUploadedFiles((prev) => [...prev, { name: fileName, content: fileContent }]);
  }, []);

  const toggleLogCell = useCallback(() => {
    setIsLogVisible((prev) => !prev);
  }, []);

  const handleLogHeightChange = useCallback((newHeight: number) => {
    setLogHeight(newHeight);
  }, []);

  const customLibraries = useMemo(
    () => installedLibraries.filter((lib) => lib.type === 'custom'),
    [installedLibraries]
  );

  const hasInitializedBodyRef = useRef(false);

  useEffect(() => {
    if (!isDag) return;

    if (templateCode && body.trim() === '' && !hasInitializedBodyRef.current) {
      setBody(templateCode);
      hasInitializedBodyRef.current = true;
    }

    return () => {
      hasInitializedBodyRef.current = false;
    };
  }, [templateCode, isDag, body]);

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.workbenchContent}>
          <LibraryCell
            target={moduleTarget}
            clientId={clientId}
            installedLibraries={installedLibraries}
            availableLibraries={availableLibraries}
            uploadedModules={uploadedModules}
            libraryDisplayNames={libraryDisplayNames}
            onInstall={handleInstallLibrary}
            onUninstall={handleUninstallLibrary}
            readOnly={readOnly}
            onFileUpload={handleFileUpload}
            onExecutionFilesUpdate={onExecutionFilesUpdate}
            executionFiles={executionFiles}
            onLibraryLog={onLibraryLog}
          />

          {isExperimentProps(props) ? (
            <div className={styles.experimentCodePanel}>
              <CodeCell code={signature} readOnly />

              <CodeCell
                code={body}
                onCodeChange={setBody}
                customLibraries={customLibraries}
                autoImports={autoImports}
                clientId={clientId}
                target={moduleTarget}
                signature={signature}
                readOnly={readOnly}
                scrollableReadOnly={scrollableReadOnlyCode}
              />
            </div>
          ) : (
            <div className={styles.dagPanel}>
              <CodeCell code={signature} style={{ flex: 1, maxHeight: 110 }} readOnly scrollableReadOnly />

              <div className={styles.codeCellContainer}>
                <CodeCell
                  code={body}
                  onCodeChange={setBody}
                  customLibraries={customLibraries}
                  autoImports={autoImports}
                  clientId={clientId}
                  target={moduleTarget}
                  signature={signature}
                  readOnly={readOnly}
                  scrollableReadOnly={scrollableReadOnlyCode}
                />
              </div>
            </div>
          )}
        </div>

        <LogCell
          logs={externalLogs}
          isVisible={isLogVisible || isExecuting}
          height={logHeight}
          minHeight={100}
          maxHeight={400}
          onToggle={toggleLogCell}
          onHeightChange={handleLogHeightChange}
          disabled={readOnly}
        />
      </div>

      {isExperimentProps(props) && (
        <WorkbenchSidebar
          initialFeatureSet={props.initialFeatureSet}
          initialParameterSet={props.initialParameterSet}
          initialDataset={props.initialDataset}
          onAssetChange={handleAssetChange}
          runId={parseInt(props.context.runId, 10)}
          experimentTags={props.experimentTags}
        />
      )}
    </div>
  );
};
